<template>
  <div class="radar-feed">
    <div class="radar-meta">
      <span>{{ c.incidents.scanner }}</span>
      <span>{{ c.incidents.range }}</span>
    </div>

    <div class="radar" aria-hidden="true">
      <div class="radar__ring r1"></div>
      <div class="radar__ring r2"></div>
      <div class="radar__ring r3"></div>
      <div class="radar__axis x"></div>
      <div class="radar__axis y"></div>
      <div class="radar__sweep"></div>
      <span v-for="(b, i) in blips" :key="i" class="radar__blip"
            :style="{ left: b.x + '%', top: b.y + '%', animationDelay: b.d + 's' }"></span>
      <span class="radar__core"></span>
    </div>

    <div class="feed">
      <div class="feed__head">
        <span><span class="feed__dot"></span>{{ c.incidents.title }}</span>
        <span class="feed__chain">DEFILLAMA</span>
      </div>
      <div class="feed__body">
        <ul v-if="state === 'loading'" :aria-label="c.incidents.loading">
          <li v-for="n in ROWS" :key="n" class="feed__row feed__row--skeleton">
            <span class="sk sk--amount"></span>
            <div class="feed__main"><span class="sk sk--name"></span><span class="sk sk--sub"></span></div>
            <span class="sk sk--time"></span>
          </li>
        </ul>
        <p v-else-if="state === 'error'" class="feed__msg">{{ c.incidents.error }}</p>
        <ul v-else>
          <li v-for="(row, i) in rows" :key="row.name + row.time + i" class="feed__row">
            <span class="feed__amount">{{ formatUsd(row.amount, lang) }}</span>
            <div class="feed__main">
              <component :is="row.source ? 'a' : 'span'" class="feed__name" :href="row.source || undefined" target="_blank" rel="noopener nofollow">{{ row.name }}</component>
              <span class="feed__sub">
                <b v-if="row.offChain" class="feed__off">{{ c.incidents.offChain }}</b>
                {{ cls(row) }}<template v-if="row.chains.length"> · {{ row.chains.join(' / ') }}</template>
              </span>
            </div>
            <span class="feed__time">{{ ago(row.time) }}</span>
          </li>
        </ul>
      </div>
      <p class="feed__source">{{ c.incidents.source }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { content as c, lang } from '@/i18n'
import { fetchIncidents, formatUsd } from '@/incidents'

// 列表固定 6 行高度：加载前后高度一致，首屏左侧文字不会跟着上下跳
const ROWS = 6
const rows = ref([])
const state = ref('loading')
const now = ref(Date.now())
let timer, clock

const blips = [
  { x: 68, y: 30, d: 0.4 }, { x: 30, y: 62, d: 1.6 }, { x: 74, y: 70, d: 2.4 }, { x: 40, y: 24, d: 3.1 },
]

const cls = row => (row.classification && c.value.incidents.classes[row.classification]) || row.classification || c.value.incidents.unknown

function ago(t) {
  const s = Math.max(1, Math.floor((now.value - t) / 1000))
  const u = c.value.feed.ago
  if (s < 3600) return Math.max(1, Math.floor(s / 60)) + u.m
  if (s < 86400) return Math.floor(s / 3600) + u.h
  return Math.floor(s / 86400) + u.d
}

// 失败自动重试（2s、5s、10s）；有旧数据就继续显示
async function load(attempt = 0, force = false) {
  try {
    const data = await fetchIncidents(force)
    rows.value = data.items.slice(0, ROWS)
    state.value = 'ok'
  } catch (e) {
    if (!rows.value.length) state.value = attempt < 3 ? 'loading' : 'error'
    if (attempt < 3) setTimeout(() => load(attempt + 1, true), [2000, 5000, 10000][attempt])
  }
}

onMounted(() => {
  load()
  // 服务端每小时更新一次，这里 15 分钟检查一次就够
  timer = setInterval(() => load(0, true), 15 * 60 * 1000)
  clock = setInterval(() => { now.value = Date.now() }, 60 * 1000)
})
onUnmounted(() => { clearInterval(timer); clearInterval(clock) })
</script>

<style scoped>
.radar-feed { position: relative; width: 100%; max-width: 480px; margin-left: auto; }
.radar-meta {
  display: flex; justify-content: space-between;
  font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; color: var(--ink-dim); margin-bottom: 14px;
}
.radar { position: relative; width: 100%; aspect-ratio: 1; max-width: 420px; margin: 0 auto -140px; }
.radar__ring { position: absolute; border: 1px solid rgba(255,255,255,.14); border-radius: 50%; }
.r1 { inset: 0; border-color: rgba(247,146,16,.35); box-shadow: 0 0 60px rgba(247,146,16,.08) inset; }
.r2 { inset: 18%; }
.r3 { inset: 36%; }
.radar__axis { position: absolute; background: rgba(255,255,255,.06); }
.radar__axis.x { left: 0; right: 0; top: 50%; height: 1px; }
.radar__axis.y { top: 0; bottom: 0; left: 50%; width: 1px; }
.radar__sweep {
  position: absolute; inset: 0; border-radius: 50%;
  background: conic-gradient(from 0deg, rgba(247,146,16,.55), rgba(247,146,16,.12) 40deg, transparent 70deg, transparent 360deg);
  animation: sweep 4s linear infinite;
  mask-image: radial-gradient(circle, #000 69%, transparent 70%);
  -webkit-mask-image: radial-gradient(circle, #000 69%, transparent 70%);
}
@keyframes sweep { to { transform: rotate(360deg); } }
.radar__core { position: absolute; left: 50%; top: 50%; width: 10px; height: 10px; margin: -5px 0 0 -5px; border-radius: 50%; background: var(--accent); box-shadow: 0 0 16px var(--accent); }
.radar__blip { position: absolute; width: 6px; height: 6px; margin: -3px; border-radius: 50%; background: var(--accent-hot); opacity: 0; animation: blip 4s infinite; }
@keyframes blip { 0%, 100% { opacity: 0; transform: scale(.6); } 10% { opacity: 1; transform: scale(1.4); } 40% { opacity: .2; transform: scale(1); } }

.feed {
  position: relative; z-index: 2;
  background: rgba(15,12,10,.86); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--ink-line);
}
.feed::before, .feed::after { content: ''; position: absolute; top: -1px; width: 14px; height: 14px; border: 2px solid var(--accent); border-bottom: 0; }
.feed::before { left: -1px; border-right: 0; }
.feed::after { right: -1px; border-left: 0; }
.feed__head {
  display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 14px 18px;
  border-bottom: 1px solid var(--ink-line);
  font-family: var(--font-mono); font-size: 11px; letter-spacing: .18em; color: var(--ink-text);
}
.feed__dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: var(--danger); margin-right: 10px; animation: pulse-r 1.6s infinite; }
@keyframes pulse-r { 50% { opacity: .3; } }
.feed__chain { color: var(--ink-dim); }
.feed__body { height: calc(var(--row-h) * 6); overflow: hidden; }
.feed { --row-h: 62px; }
.feed__msg { padding: 24px 18px; color: var(--ink-dim); font-size: 14px; }
.sk { display: block; border-radius: 2px; background: linear-gradient(90deg, rgba(255,255,255,.05), rgba(255,255,255,.11), rgba(255,255,255,.05)); background-size: 200% 100%; animation: shimmer 1.4s infinite linear; }
.sk--amount { width: 64px; height: 16px; flex: none; margin-left: 12px; }
.sk--name { width: 58%; height: 13px; margin-bottom: 7px; }
.sk--sub { width: 80%; height: 10px; }
.sk--time { width: 28px; height: 10px; }
@keyframes shimmer { to { background-position: -200% 0; } }
.feed__row { display: flex; align-items: center; gap: 14px; height: var(--row-h); padding: 0 18px; border-bottom: 1px solid rgba(255,255,255,.04); box-sizing: border-box; }
.feed__amount {
  flex: none; min-width: 76px; text-align: right;
  font-family: var(--font-display); font-size: 16px; font-weight: 700; color: var(--danger); white-space: nowrap;
}
.feed__main { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.feed__name { font-size: 14px; color: var(--ink-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
a.feed__name:hover { color: var(--accent); }
.feed__sub { font-size: 11px; color: var(--ink-dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.feed__off { font-family: var(--font-mono); font-weight: 500; font-size: 10px; letter-spacing: .08em; color: #140a00; background: var(--accent); padding: 1px 5px; margin-right: 6px; }
.feed__time { font-family: var(--font-mono); font-size: 11px; color: var(--ink-dim); white-space: nowrap; }
.feed__source { padding: 10px 18px; font-size: 11px; color: var(--ink-dim); border-top: 1px solid var(--ink-line); }

@media (max-width: 900px) {
  .radar-feed { margin: 0 auto; }
  .radar { max-width: 300px; margin-bottom: -100px; }
}
@media (max-width: 420px) {
  .feed__amount { min-width: 64px; font-size: 14px; }
  .feed__row { gap: 10px; padding: 0 14px; }
}
</style>
